/**
 * Ported 1:1 from z1-ui-main `RawDataDialog/manufacturers/cerebrumx/parseCerebrumxPayload.ts`
 * (types stripped). Same `cx_*` schema px-ingest-http parses.
 */
const record = (value) =>
	typeof value === 'object' && value !== null && !Array.isArray(value) ? value : undefined;

const num = (value) => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);

const str = (value) => (typeof value === 'string' && value.length > 0 ? value : undefined);

const bool = (value) => (typeof value === 'boolean' ? value : undefined);

/**
 * `{ value, unit }` → measure; a missing or non-numeric value drops the whole measure.
 * `defaultUnit` applies when CerebrumX omits the unit of a field whose unit is fixed.
 */
const measure = (value, defaultUnit) => {
	const pair = record(value);
	const amount = num(pair?.value);
	if (amount === undefined) return undefined;
	return { value: amount, unit: str(pair?.unit) ?? defaultUnit };
};

/** A parsable timestamp (CerebrumX serves microseconds) normalized to millisecond ISO. */
const isoTime = (value) => {
	const text = str(value);
	const ms = text === undefined ? Number.NaN : Date.parse(text);
	if (Number.isNaN(ms)) return undefined;
	return new Date(ms).toISOString();
};

/** `cx_readable_timestamp` first, `cx_timestamp` (epoch seconds) as fallback. */
const eventTime = (payload) => {
	const readable = isoTime(payload.cx_readable_timestamp);
	if (readable !== undefined) return readable;
	const epochSeconds = num(payload.cx_timestamp);
	if (epochSeconds === undefined) return undefined;
	return new Date(epochSeconds * 1000).toISOString();
};

/** Breadcrumbs, in wire order; entries that are not objects are skipped. */
const samples = (value) => {
	if (!Array.isArray(value)) return undefined;
	return value.flatMap((entry) => {
		const sample = record(entry);
		if (sample === undefined) return [];
		const geolocation = record(sample.cx_geolocation);
		const acceleration = record(sample.cx_acc);
		return [
			{
				time: isoTime(sample.cx_readable_timestamp),
				latitude: num(geolocation?.cx_latitude),
				longitude: num(geolocation?.cx_longitude),
				speed: measure(sample.cx_vehicle_speed),
				acceleration: measure(acceleration?.acceleration),
				lateralAcceleration: measure(acceleration?.accelerationlat),
			},
		];
	});
};

const tire = (block, code) => ({
	pressure: num(block[`cx_${code}_tirepress`]),
	placard: num(block[`cx_${code}_tirepress_placard`]),
	status: str(block[`cx_${code}_tirepress_status`]),
});

const tires = (value) => {
	const block = record(value);
	if (block === undefined) return undefined;
	return {
		frontLeft: tire(block, 'fl'),
		frontRight: tire(block, 'fr'),
		rearLeft: tire(block, 'rl'),
		rearRight: tire(block, 'rr'),
	};
};

/** The model year is served as a string ('2025') but tolerate a number. */
const year = (value) => {
	const numeric = num(value);
	if (numeric === undefined) return str(value);
	return String(numeric);
};

/**
 * Lifts the rendered fields off a CerebrumX JSON payload. Null when the
 * payload is not an object at all (the tab then says it is unreadable);
 * otherwise every field is individually optional.
 */
export const parseCerebrumxPayload = (payload) => {
	const p = record(payload);
	if (p === undefined) return null;
	const geolocation = record(p.cx_geolocation);
	const acceleration = record(p.cx_acc);
	const fuel = record(p.cx_fuel);
	const engine = record(p.cx_engine);
	const battery = record(p.cx_battery);
	return {
		eventType: str(p.cx_event_type),
		eventTime: eventTime(p),
		sentAt: str(p.cx_sending_timestamp),
		messageId: str(p.cx_msg_id),
		tripId: str(p.cx_trip_id),
		vin: str(p.cx_vehicle_id),
		make: str(p.cx_make),
		model: str(p.cx_model),
		trim: str(p.cx_trim),
		year: year(p.cx_year),
		moving: bool(p.cx_moving),
		gear: str(p.cx_gear_position),
		speed: measure(p.cx_vehicle_speed),
		speedGps: measure(p.cx_vehicle_speed_gps),
		odometer: measure(p.cx_odometer),
		tripDistance: num(p.cx_trip_distance),
		acceleration: measure(acceleration?.acceleration),
		lateralAcceleration: measure(acceleration?.accelerationlat),
		latitude: num(geolocation?.cx_latitude),
		longitude: num(geolocation?.cx_longitude),
		altitude: measure(geolocation?.cx_altitude),
		heading: num(geolocation?.cx_heading),
		gpsError: num(geolocation?.cx_geolocation_error),
		gpsInvalid: bool(geolocation?.cx_geolocation_isinvalid),
		evLevel: num(p.cx_ev_level),
		absEvLevel: num(p.cx_abs_ev_level),
		fuelLevel: measure(fuel?.cx_fuel_level),
		absFuelLevel: measure(fuel?.cx_abs_fuel_level),
		distanceToEmpty: measure(fuel?.cx_distance_to_empty),
		// Served without a unit; it is litres (prod 2026-10-23: 7.8 l at 8.6 % → ~90.7, capacity 90.8).
		fuelTankCapacity: measure(fuel?.cx_fuel_tank_capacity, 'l'),
		instantConsumption: measure(fuel?.cx_instantconsumption),
		// Also served without a unit: cumulative litres, like the rest of the fuel block.
		totalFuelConsumed: measure(fuel?.cx_total_fuel_consumed, 'l'),
		totalFuelEconomy: measure(fuel?.cx_total_fuel_economy),
		batteryVoltage: measure(battery?.cx_battery_level),
		ambientTemperature: measure(p.cx_ambient_temperature),
		coolantTemperature: measure(engine?.cx_coolant_temp),
		engineSpeed: measure(engine?.cx_engine_speed),
		ignitionStatus: num(engine?.cx_ign_status),
		oilLife: measure(engine?.cx_remaining_oil_life),
		engineTime: measure(engine?.cx_total_engine_time),
		tires: tires(p.cx_tires),
		samples: samples(p.cx_telemetry_data),
	};
};
